import React, { useState, useContext, useEffect } from 'react';
import { Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, Select, MenuItem, InputLabel, FormControl, Typography, List, ListItem, ListItemText, IconButton } from '@mui/material';
import { Delete } from '@mui/icons-material';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import BibleLinkSelector from './BibleLinkSelector';

const QuestionForm = ({ open, handleClose, addQuestion, editQuestion, question }) => {
    const { auth } = useContext(AuthContext);
    const [questionText, setQuestionText] = useState('');
    const [questionType, setQuestionType] = useState('');
    const [answers, setAnswers] = useState([{ answer_text: '' }, { answer_text: '' }, { answer_text: '' }, { answer_text: '' }]);
    const [bibleLinks, setBibleLinks] = useState([]);
    const [bibleLinkSelectorOpen, setBibleLinkSelectorOpen] = useState(false);

    const questionTypes = ['Buttons', 'TrueFalse', 'TypeIn'];

    useEffect(() => {
        if (question) {
            setQuestionText(question.question_text);
            setQuestionType(question.question_type);
            setBibleLinks(question.links || []);
            if (question.question_type === 'Buttons' || question.question_type === 'TrueFalse' || question.question_type === 'TypeIn') {
                axios.get(`/api/questions/${question.id}/answers`, {
                    headers: {
                        'x-auth-token': auth.token
                    }
                }).then(res => {
                    const fetchedAnswers = res.data;
                    let newAnswers;
                    if (question.question_type === 'TrueFalse') {
                        // For editing TrueFalse, use fetched answers, ensure one correct and one incorrect
                        const correctAns = fetchedAnswers.find(ans => ans.is_correct);
                        const incorrectAns = fetchedAnswers.find(ans => !ans.is_correct);
                        newAnswers = [
                            correctAns ? correctAns : { answer_text: '', is_correct: true },
                            incorrectAns ? incorrectAns : { answer_text: '', is_correct: false }
                        ];
                    } else if (question.question_type === 'TypeIn') {
                        const correctAns = fetchedAnswers.find(ans => ans.is_correct);
                        newAnswers = [correctAns ? correctAns : { answer_text: '', is_correct: true }];
                    } else { // Buttons
                        newAnswers = [{ answer_text: '' }, { answer_text: '' }, { answer_text: '' }, { answer_text: '' }];
                        fetchedAnswers.forEach((answer, index) => {
                            newAnswers[index] = answer;
                        });
                    }
                    setAnswers(newAnswers);
                });
            }
        } else { // New question
            setQuestionText('');
            setBibleLinks([]);
            // When creating a new question, set initial answers based on type
            if (questionType === 'TrueFalse') {
                setAnswers([
                    { answer_text: '', is_correct: true },
                    { answer_text: '', is_correct: false }
                ]);
            } else if (questionType === 'TypeIn') {
                setAnswers([
                    { answer_text: '', is_correct: true }
                ]);
            } else {
                setAnswers([{ answer_text: '' }, { answer_text: '' }, { answer_text: '' }, { answer_text: '' }]); // Default for Buttons
            }
        }
    }, [question, auth.token, questionType]); // Added questionType as a dependency

    const handleOpenBibleLinkSelector = () => {
        setBibleLinkSelectorOpen(true);
    };

    const handleCloseBibleLinkSelector = () => {
        setBibleLinkSelectorOpen(false);
    };

    const handleAddBibleLink = async (newLinkDataArray) => {
        try {
            if (question && question.id) {
                // Existing question: make API calls for each link
                const addedLinks = [];
                for (const newLinkData of newLinkDataArray) {
                    const res = await axios.post(`/api/questions/${question.id}/links`, newLinkData, {
                        headers: {
                            'x-auth-token': auth.token
                        }
                    });
                    addedLinks.push(res.data);
                }
                const updatedLinks = [...bibleLinks, ...addedLinks];
                setBibleLinks(updatedLinks);
                editQuestion({ ...question, links: updatedLinks });
            } else {
                // New question: update local state with all new links
                const newLinksWithTempIds = newLinkDataArray.map(link => ({ ...link, id: Date.now() + Math.random() })); // Assign a temporary unique ID for each
                const updatedLinks = [...bibleLinks, ...newLinksWithTempIds];
                setBibleLinks(updatedLinks);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteLink = async (linkId) => {
        try {
            if (question && question.id) {
                // Existing question: make API call
                await axios.delete(`/api/questions/links/${linkId}`, {
                    headers: {
                        'x-auth-token': auth.token
                    }
                });
                const updatedLinks = bibleLinks.filter(link => link.id !== linkId);
                setBibleLinks(updatedLinks);
                editQuestion({ ...question, links: updatedLinks });
            } else {
                // New question: update local state
                const updatedLinks = bibleLinks.filter(link => link.id !== linkId);
                setBibleLinks(updatedLinks);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleAnswerChange = (index, value) => {
        const newAnswers = [...answers];
        newAnswers[index].answer_text = value;
        setAnswers(newAnswers);
    };

    const handleChangeQuestionType = (e) => {
        const newType = e.target.value;
        setQuestionType(newType);
        if (newType === 'TrueFalse') {
            setAnswers([
                { answer_text: '', is_correct: true }, // Default to blank for editable
                { answer_text: '', is_correct: false } // Default to blank for editable
            ]);
        } else if (newType === 'TypeIn') {
            setAnswers([
                { answer_text: '', is_correct: true }
            ]);
        } else {
            setAnswers([{ answer_text: '' }, { answer_text: '' }, { answer_text: '' }, { answer_text: '' }]);
        }
    };

    const handleSubmit = async () => {
        if (questionType === 'Buttons' || questionType === 'TrueFalse' || questionType === 'TypeIn') {
            for (const answer of answers) {
                if (!answer.answer_text.trim()) {
                    alert('Пожалуйста, заполните все ответы.');
                    return;
                }
            }
        }

        try {
            let res;
            if (question) {
                res = await axios.put(`/api/questions/${question.id}`, {
                    question_text: questionText,
                    question_type: questionType
                }, {
                    headers: {
                        'x-auth-token': auth.token
                    }
                });
                const updatedQuestion = { ...res.data, links: bibleLinks };
                const newAnswers = [];

                if (questionType === 'Buttons') {
                    for (let i = 0; i < answers.length; i++) {
                        const answer = answers[i];
                        const isCorrect = i === 0;
                        let answerRes;
                        if (answer.id) {
                            answerRes = await axios.put(`/api/questions/answers/${answer.id}`, {
                                answer_text: answer.answer_text,
                                is_correct: isCorrect
                            }, {
                                headers: {
                                    'x-auth-token': auth.token
                                }
                            });
                        } else {
                            answerRes = await axios.post(`/api/questions/${question.id}/answers`, {
                                answer_text: answer.answer_text,
                                is_correct: isCorrect
                            }, {
                                headers: {
                                    'x-auth-token': auth.token
                                }
                            });
                        }
                        newAnswers.push(answerRes.data);
                    }
                } else if (questionType === 'TrueFalse') {
                    // Find existing true/false answers or create new ones
                    const existingTrue = answers.find(ans => ans.is_correct);
                    const existingFalse = answers.find(ans => !ans.is_correct);

                    // Process 'True' answer
                    if (existingTrue && existingTrue.id) {
                        const trueRes = await axios.put(`/api/questions/answers/${existingTrue.id}`, {
                            answer_text: existingTrue.answer_text,
                            is_correct: true
                        }, { headers: { 'x-auth-token': auth.token } });
                        newAnswers.push(trueRes.data);
                    } else {
                        const trueRes = await axios.post(`/api/questions/${question.id}/answers`, {
                            answer_text: '', // Default to blank if not found
                            is_correct: true
                        }, { headers: { 'x-auth-token': auth.token } });
                        newAnswers.push(trueRes.data);
                    }

                    // Process 'False' answer
                    if (existingFalse && existingFalse.id) {
                        const falseRes = await axios.put(`/api/questions/answers/${existingFalse.id}`, {
                            answer_text: existingFalse.answer_text,
                            is_correct: false
                        }, { headers: { 'x-auth-token': auth.token } });
                        newAnswers.push(falseRes.data);
                    } else {
                        const falseRes = await axios.post(`/api/questions/${question.id}/answers`, {
                            answer_text: '', // Default to blank if not found
                            is_correct: false
                        }, { headers: { 'x-auth-token': auth.token } });
                        newAnswers.push(falseRes.data);
                    }
                } else if (questionType === 'TypeIn') {
                    const existingAnswer = answers[0];

                    if (existingAnswer && existingAnswer.id) {
                        const typeInRes = await axios.put(`/api/questions/answers/${existingAnswer.id}`, {
                            answer_text: existingAnswer.answer_text,
                            is_correct: true
                        }, { headers: { 'x-auth-token': auth.token } });
                        newAnswers.push(typeInRes.data);
                    } else {
                        const typeInRes = await axios.post(`/api/questions/${question.id}/answers`, {
                            answer_text: existingAnswer.answer_text || '',
                            is_correct: true
                        }, { headers: { 'x-auth-token': auth.token } });
                        newAnswers.push(typeInRes.data);
                    }
                }
                editQuestion({ ...updatedQuestion, answers: newAnswers }); // Single call here

            } else { // creating new question
                res = await axios.post('/api/questions', {
                    question_text: questionText,
                    question_type: questionType,
                    bibleLinks: bibleLinks.map(({id, ...link}) => link) // Exclude temporary 'id' for new links
                }, {
                    headers: {
                        'x-auth-token': auth.token
                    }
                });
                const newQuestion = { ...res.data, links: bibleLinks };
                const newAnswers = [];

                if (questionType === 'Buttons') {
                    const newAnswers = [];
                    for (let i = 0; i < answers.length; i++) {
                        const answerRes = await axios.post(`/api/questions/${newQuestion.id}/answers`, {
                            answer_text: answers[i].answer_text,
                            is_correct: i === 0
                        }, {
                            headers: {
                                'x-auth-token': auth.token
                            }
                        });
                        newAnswers.push(answerRes.data);
                    }
                    addQuestion({ ...newQuestion, answers: newAnswers });
                } else if (questionType === 'TrueFalse') {
                    const newAnswers = [];
                    // Ensure the 'True' answer is sent as correct and 'False' as incorrect
                    const trueAnswer = answers.find(ans => ans.is_correct) || { answer_text: '' };
                    const falseAnswer = answers.find(ans => !ans.is_correct) || { answer_text: '' };

                    const trueRes = await axios.post(`/api/questions/${newQuestion.id}/answers`, {
                        answer_text: trueAnswer.answer_text,
                        is_correct: true
                    }, {
                        headers: {
                            'x-auth-token': auth.token
                        }
                    });
                    newAnswers.push(trueRes.data);

                    const falseRes = await axios.post(`/api/questions/${newQuestion.id}/answers`, {
                        answer_text: falseAnswer.answer_text,
                        is_correct: false
                    }, {
                        headers: {
                            'x-auth-token': auth.token
                        }
                    });
                    newAnswers.push(falseRes.data);
                    addQuestion({ ...newQuestion, answers: newAnswers });
                } else if (questionType === 'TypeIn') {
                    const newAnswers = [];
                    const typeInAnswer = answers[0] || { answer_text: '' };

                    const typeInRes = await axios.post(`/api/questions/${newQuestion.id}/answers`, {
                        answer_text: typeInAnswer.answer_text,
                        is_correct: true
                    }, {
                        headers: {
                            'x-auth-token': auth.token
                        }
                    });
                    newAnswers.push(typeInRes.data);
                    addQuestion({ ...newQuestion, answers: newAnswers });
                } else {
                    addQuestion(newQuestion);
                }
            }
            
            handleClose();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>{question ? 'Редактировать вопрос' : 'Создать вопрос'}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Текст вопроса"
                    type="text"
                    fullWidth
                    multiline
                    rows={4}
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                />
                <FormControl fullWidth margin="dense">
                    <InputLabel id="question-type-label">Тип вопроса</InputLabel>
                    <Select
                        labelId="question-type-label"
                        id="question-type-select"
                        value={questionType}
                        label="Тип вопроса"
                        onChange={handleChangeQuestionType}
                    >
                        {questionTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                                {type}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {questionType === 'Buttons' && (
                    <div>
                        {answers.map((answer, index) => (
                            <TextField
                                key={index}
                                margin="dense"
                                label={`Ответ ${index + 1}`}
                                type="text"
                                fullWidth
                                multiline
                                rows={2}
                                value={answer.answer_text}
                                onChange={(e) => handleAnswerChange(index, e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: index === 0 ? 'green' : 'red',
                                        borderWidth: '2px',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: index === 0 ? 'green' : 'red',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: index === 0 ? 'green' : 'red',
                                    },
                                }}
                            />
                        ))}
                    </div>
                )}
                {questionType === 'TrueFalse' && (
                    <div>
                        {answers.map((answer, index) => (
                            <TextField
                                key={index}
                                margin="dense"
                                label={answer.is_correct ? 'Правильный ответ' : 'Неправильный ответ'}
                                type="text"
                                fullWidth
                                multiline
                                rows={2}
                                value={answer.answer_text}
                                onChange={(e) => handleAnswerChange(index, e.target.value)} // Still allow changing text
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: answer.is_correct ? 'green' : 'red',
                                        borderWidth: '2px',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: answer.is_correct ? 'green' : 'red',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: answer.is_correct ? 'green' : 'red',
                                    },
                                }}
                            />
                        ))}
                    </div>
                )}
                {questionType === 'TypeIn' && (
                    <div>
                        <TextField
                            margin="dense"
                            label="Правильный ответ"
                            type="text"
                            fullWidth
                            multiline
                            rows={2}
                            value={answers[0]?.answer_text || ''}
                            onChange={(e) => handleAnswerChange(0, e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'green',
                                    borderWidth: '2px',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'green',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'green',
                                },
                            }}
                        />
                    </div>
                )}
                {/* Bible Links Section */}
                <div>
                    <Typography variant="h6" sx={{ mt: 2 }}>Связанные стихи Библии</Typography>
                    {bibleLinks.length === 0 ? (
                        <Typography variant="body2">Нет связанных стихов.</Typography>
                    ) : (
                        <List dense>
                            {bibleLinks.map((link) => (
                                <ListItem key={link.id}>
                                    <ListItemText primary={`${link.book_name} ${link.chapter}:${link.verse}`} />
                                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteLink(link.id)}>
                                        <Delete />
                                    </IconButton>
                                </ListItem>
                            ))}
                        </List>
                    )}
                    <Button variant="outlined" sx={{ mt: 1 }} onClick={handleOpenBibleLinkSelector}>
                        Добавить ссылку на стих
                    </Button>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Отмена</Button>
                <Button onClick={handleSubmit}>{question ? 'Сохранить' : 'Создать'}</Button>
            </DialogActions>
            <BibleLinkSelector
                open={bibleLinkSelectorOpen}
                handleClose={handleCloseBibleLinkSelector}
                onSelectLink={handleAddBibleLink}
            />
        </Dialog>
    );
};

export default QuestionForm;


